import { useMemo, useState } from "react";
import { errorMessage } from "@/lib/api";
import type { MeTeam, Player } from "@/lib/types";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/Button";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useAthleteDetail } from "@/features/athletes/hooks/useAthletes";
import {
  REJOIN_LABEL_SUFFIX,
  rosterTargets,
} from "@/features/athletes/athleteDisplay";
import { CoachModal } from "./CoachModal";
import type { RosterMutations } from "../hooks/useRoster";
import { teamSeasonLabel } from "@/lib/teamSeasonLabel";

/**
 * Transfer — Coach > Roster (#908 §6 step 5).
 *
 * Ends this assignment and creates the same athlete's assignment on the
 * destination team in one request; jersey, position and avatar carry
 * over, the coach's roster notes do not. `teams` is the coach's own list
 * of non-archived teams they hold `roster:manage` on (from `/api/me`
 * `teams[]`), minus the current one — the backend needs that capability
 * on both ends. Teams where the athlete already holds an ACTIVE assignment
 * (from the athlete detail) are removed as well — the backend refuses
 * (409) a transfer there — while a team with only an ENDED assignment is
 * offered as a rejoin: the backend reinstates that row. Dates are
 * optional; the service stamps today otherwise.
 */

/** "Team · Season"; a season-less (legacy) team reads "Team · Legacy". */
export function teamOptionLabel(team: MeTeam): string {
  return teamSeasonLabel(team.name, team.season?.name, "Legacy");
}

export type TransferPlayerModalProps = {
  player: Player;
  /** Candidate destination teams (already excludes the current team);
   * the athlete's other assignment teams are filtered out here. */
  teams: MeTeam[];
  mutations: RosterMutations;
  onClose: () => void;
  onTransferred?: () => void;
};

export function TransferPlayerModal({
  player,
  teams: candidateTeams,
  mutations,
  onClose,
  onTransferred,
}: TransferPlayerModalProps) {
  const toast = useToast();
  const detailQuery = useAthleteDetail(player.athlete_id);
  const assignments = detailQuery.data?.assignments;
  const targets = useMemo(
    () => rosterTargets(candidateTeams, assignments),
    [candidateTeams, assignments],
  );
  const teams = useMemo(() => targets.map(({ team }) => team), [targets]);
  const [pickedTeamId, setToTeamId] = useState("");
  // A pick that the athlete-detail filter later removed falls back to
  // "none" (or the only remaining team).
  const toTeamId = teams.some((team) => team.id === pickedTeamId)
    ? pickedTeamId
    : teams.length === 1
      ? teams[0].id
      : "";
  const [endsOn, setEndsOn] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!toTeamId) {
      setError("Pick the team to transfer to.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await mutations.transferAssignment(player.id, {
        to_team_id: toTeamId,
        ends_on: endsOn || null,
        starts_on: startsOn || null,
      });
      const destination = teams.find((t) => t.id === toTeamId);
      toast.success(
        destination
          ? `${player.display_name} transferred to ${destination.name}`
          : "Player transferred",
      );
      onTransferred?.();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      toast.error("Couldn't transfer player", {
        description: errorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CoachModal
      title={`Transfer · ${player.display_name}`}
      description="Ends this team's assignment and starts one on the destination team. The athlete, their photo and account links carry over."
      onClose={onClose}
      data-testid="roster-transfer-modal"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="roster-transfer-form"
            disabled={submitting || detailQuery.isLoading || teams.length === 0}
            data-testid="roster-transfer-submit"
          >
            {submitting ? "Transferring…" : "Transfer"}
          </Button>
        </div>
      }
    >
      <form
        id="roster-transfer-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Destination team *
          </span>
          <TkticSelect
            value={toTeamId}
            onChange={(next) => {
              setToTeamId(next);
              setError(null);
            }}
            data-testid="roster-transfer-team"
            ariaLabel="Destination team"
            options={[
              {
                value: "",
                label:
                  teams.length === 0
                    ? "No other team available"
                    : "Select a team…",
              },
              ...targets.map(({ team, rejoin }) => ({
                value: team.id,
                label: `${teamOptionLabel(team)}${rejoin ? REJOIN_LABEL_SUFFIX : ""}`,
              })),
            ]}
          />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
              Ends here on
              <span className="ml-1 normal-case tracking-normal text-ink-muted/70">
                (optional)
              </span>
            </span>
            <TkticDateInput
              value={endsOn}
              onChange={setEndsOn}
              data-testid="roster-transfer-ends-on"
              buttonAriaLabel="Pick the end date"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
              Starts there on
              <span className="ml-1 normal-case tracking-normal text-ink-muted/70">
                (optional)
              </span>
            </span>
            <TkticDateInput
              value={startsOn}
              onChange={setStartsOn}
              data-testid="roster-transfer-starts-on"
              buttonAriaLabel="Pick the start date"
            />
          </label>
        </div>
        <p
          className="text-xs text-ink-muted"
          data-testid="roster-transfer-consequence"
        >
          This removes the athlete's access to this season's team content.
        </p>
        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="roster-transfer-error"
          >
            {error}
          </p>
        ) : null}
      </form>
    </CoachModal>
  );
}
